import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import { ArrowUpRight, Atom, BadgeCheck, Globe2, Handshake, Sparkles, Zap } from "lucide-react";
import InOutBioBrand from "../components/InOutBioBrand";

export default function Products() {
    const location = useLocation();

    useEffect(() => {
        if (!location.hash) return;

        const section = document.getElementById(location.hash.slice(1));
        if (section) {
            requestAnimationFrame(() => section.scrollIntoView({ behavior: "smooth", block: "start" }));
        }
    }, [location.hash]);

    return (
        <div className="bg-gray-50 min-h-screen">
            {/* Header - Matching Services Page style */}
            <section className="relative py-24 px-6 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"></div>
                <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-blue-900/20 to-transparent"></div>
                <div className="absolute bottom-0 left-0 w-1/2 h-full bg-gradient-to-r from-purple-900/20 to-transparent"></div>

                <div className="max-w-4xl mx-auto text-center relative z-10">
                    <motion.span
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="text-blue-400 font-semibold tracking-wider uppercase text-sm"
                    >
                        Our Products
                    </motion.span>
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-5xl md:text-6xl font-extrabold text-white mt-4 mb-6 tracking-tight"
                    >
                        Products for Discovery & Collaboration
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-xl text-slate-300 max-w-2xl mx-auto font-light leading-relaxed"
                    >
                        Purpose-built platforms that advance molecular design and connect promising science with the right partners.
                    </motion.p>
                </div>
            </section>

            <div className="max-w-7xl mx-auto px-6 py-20">
                <div className="flex items-center gap-4 mb-16">
                    <div className="h-px bg-slate-300 flex-1"></div>
                    <h2 className="text-3xl font-bold text-slate-800 uppercase tracking-widest text-center px-4">AI Molecular Design</h2>
                    <div className="h-px bg-slate-300 flex-1"></div>
                </div>

                {/* Product: SmartGen */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-28">
                    <div className="order-2 lg:order-1">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="p-4 bg-primary-600 text-white rounded-xl shadow-sm">
                                <Atom size={32} />
                            </div>
                            <div>
                                <span className="text-sm text-primary-600 font-semibold">AI-Powered Platform</span>
                                <h2 className="text-4xl font-bold text-gray-900">SmartGen</h2>
                            </div>
                        </div>

                        <p className="text-lg text-gray-700 leading-relaxed mb-8">
                            AI pipeline for Generating novel chemical compound specific for the therapeutic target of interest.
                        </p>

                        <div className="space-y-4 mb-8">
                            <div className="flex items-start gap-4 p-4 bg-white rounded-xl border border-primary-100 hover:border-primary-300 transition-colors shadow-sm">
                                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center shrink-0">
                                    <Sparkles size={20} className="text-primary-600" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-900 mb-1">In-Pocket Generation</h4>
                                    <p className="text-sm text-gray-600">Generate molecules that fit specific protein pockets with high precision.</p>
                                </div>
                            </div>

                            <div className="flex items-start gap-4 p-4 bg-white rounded-xl border border-purple-100 hover:border-purple-300 transition-colors shadow-sm">
                                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center shrink-0">
                                    <Zap size={20} className="text-purple-600" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-900 mb-1">Multi-Objective Optimization</h4>
                                    <p className="text-sm text-gray-600">Optimize for potency, solubility, and toxicity simultaneously.</p>
                                </div>
                            </div>

                            <div className="flex items-start gap-4 p-4 bg-white rounded-xl border border-indigo-100 hover:border-indigo-300 transition-colors shadow-sm">
                                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center shrink-0">
                                    <Atom size={20} className="text-indigo-600" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-900 mb-1">Synthesizability First</h4>
                                    <p className="text-sm text-gray-600">Ensures all generated compounds are chemically feasible.</p>
                                </div>
                            </div>
                        </div>

                        <button className="px-8 py-3.5 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-colors shadow-sm">
                            Request Demo
                        </button>
                    </div>

                    <div className="order-1 lg:order-2">
                        <div className="relative bg-white p-6 rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500"></div>
                            <div className="h-96 overflow-hidden rounded-xl">
                                <img
                                    src="/images/smartgen_visual.png"
                                    alt="SmartGen Interface"
                                    className="w-full h-full object-cover"
                                    style={{ objectPosition: '50% 25%' }}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-4 mb-16">
                    <div className="h-px bg-slate-300 flex-1"></div>
                    <h2 className="text-3xl font-bold text-slate-800 uppercase tracking-widest text-center px-4">External Innovation</h2>
                    <div className="h-px bg-slate-300 flex-1"></div>
                </div>

                {/* Product: InOutBio */}
                <motion.section
                    id="inoutbio"
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.15 }}
                    className="overflow-hidden bg-[#0d211c] text-[#fdfdf9] scroll-mt-28"
                >
                    <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] min-h-[620px]">
                        <div className="px-7 py-12 md:p-14 lg:p-16 flex flex-col justify-between">
                            <div>
                                <InOutBioBrand className="text-[#fdfdf9] text-2xl mb-12 hover:opacity-80 transition-opacity" />

                                <p className="text-[#d99a36] text-xs font-bold tracking-[0.24em] uppercase mb-5">
                                    The home of external innovations
                                </p>
                                <h2 className="font-['InOutBioPoppins'] font-bold text-5xl md:text-6xl leading-[1.03] tracking-[-0.045em] mb-7">
                                    Where science finds its way forward<span className="text-[#d99a36]">.</span>
                                </h2>
                                <p className="text-lg leading-relaxed text-[#fdfdf9]/70 max-w-xl">
                                    InOutBio gives research assets a credible path to visibility. Its reviewed global network helps asset owners present opportunities clearly and helps potential partners discover science ready for a meaningful conversation.
                                </p>
                            </div>

                            <div className="mt-12 flex flex-wrap gap-4">
                                <a
                                    href="https://inoutbio.com/register"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="group inline-flex items-center gap-3 bg-[#d99a36] text-[#0d211c] px-7 py-4 font-bold hover:bg-[#e4ad51] transition-colors"
                                    aria-label="Find opportunities on InOutBio in a new tab"
                                >
                                    Find opportunities
                                    <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                                </a>
                                <a
                                    href="https://inoutbio.com/start/post-asset"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="group inline-flex items-center gap-3 border border-[#fdfdf9]/30 px-7 py-4 font-semibold hover:border-[#fdfdf9]/70 transition-colors"
                                    aria-label="Submit an asset to InOutBio in a new tab"
                                >
                                    Submit an asset
                                    <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                                </a>
                            </div>
                        </div>

                        <div className="relative min-h-[380px] lg:min-h-full overflow-hidden">
                            <motion.img
                                initial={{ scale: 1.05 }}
                                whileInView={{ scale: 1 }}
                                viewport={{ once: true }}
                                transition={{ duration: 1.2, ease: "easeOut" }}
                                src="/images/inoutbio-lab.png"
                                alt="Scientist inspecting a laboratory microplate"
                                className="absolute inset-0 h-full w-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#0d211c]/55 via-transparent to-transparent lg:bg-gradient-to-r lg:from-[#0d211c]/25 lg:to-transparent"></div>
                        </div>
                    </div>

                    <div className="border-t border-[#fdfdf9]/15 grid grid-cols-1 md:grid-cols-3">
                        <div className="p-8 md:p-10 border-b md:border-b-0 md:border-r border-[#fdfdf9]/15">
                            <BadgeCheck className="text-[#d99a36] mb-6" size={28} strokeWidth={1.6} />
                            <h3 className="text-xl font-semibold mb-3">Reviewed visibility</h3>
                            <p className="text-sm leading-relaxed text-[#fdfdf9]/65">
                                Research assets are presented through a reviewed publication layer designed to build clarity and credibility.
                            </p>
                        </div>
                        <div className="p-8 md:p-10 border-b md:border-b-0 md:border-r border-[#fdfdf9]/15">
                            <Globe2 className="text-[#d99a36] mb-6" size={28} strokeWidth={1.6} />
                            <h3 className="text-xl font-semibold mb-3">Global opportunity discovery</h3>
                            <p className="text-sm leading-relaxed text-[#fdfdf9]/65">
                                Search and surface scientific opportunities across a focused international external-innovation network.
                            </p>
                        </div>
                        <div className="p-8 md:p-10">
                            <Handshake className="text-[#d99a36] mb-6" size={28} strokeWidth={1.6} />
                            <h3 className="text-xl font-semibold mb-3">Controlled engagement</h3>
                            <p className="text-sm leading-relaxed text-[#fdfdf9]/65">
                                Move promising assets toward the right partnering conversation while keeping engagement purposeful and controlled.
                            </p>
                        </div>
                    </div>

                    <div className="border-t border-[#fdfdf9]/15 px-7 py-6 md:px-10 flex flex-wrap gap-x-8 gap-y-3 text-[11px] uppercase tracking-[0.16em] text-[#fdfdf9]/55">
                        <span><span className="text-[#d99a36] mr-2">✓</span>Free submission</span>
                        <span><span className="text-[#d99a36] mr-2">✓</span>Reviewed publication</span>
                        <span><span className="text-[#d99a36] mr-2">✓</span>Controlled engagement</span>
                    </div>
                </motion.section>
            </div>
        </div>
    );
}
